import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { IconField } from "@/components/beta/IconField";
import {
  buildAppEntryUrl,
  recordBetaEvent,
  recordEngagedVisitIfNeeded,
  recordModuleZoom,
  recordPageView,
  type BetaEventType,
} from "@/lib/betaWaitlistApi";
import { publicAssetUrl } from "@/lib/publicAssetUrl";
import { BETA_MODULES, ICON_FIELD_MODULES, type BetaModule } from "@/shared/config/betaModules";

type ModuleGroup = "command" | "live" | "prep" | "players";
type AudioAction = "opened" | "played" | "paused" | "stopped" | "closed" | "completed";

const AUDIO_EVENT_TYPES: Record<AudioAction, BetaEventType> = {
  opened: "founder_message_opened",
  played: "founder_message_played",
  paused: "founder_message_paused",
  stopped: "founder_message_stopped",
  closed: "founder_message_closed",
  completed: "founder_message_completed",
};

const FOUNDER_OPENED_AT_KEY = "rw-founder-message-opened-at";
const FOUNDER_SESSION_ID_KEY = "rw-founder-message-session-id";
const FOUNDER_STARTED_KEY = "rw-founder-message-started";
const FOUNDER_COMPLETED_KEY = "rw-founder-message-completed";

type AudioEngagement = {
  positionSeconds: number;
  listenedSeconds: number;
  durationSeconds: number;
  percentComplete: number;
};

const MODULE_ORDER = [
  "dashboard",
  "guardians-eye",
  "visbys-portal",
  "wardens-vanguard",
  "content-intelligence",
  "omnarium",
  "encounter-crucible",
  "heros-loom",
  "wardens-echo",
  "adventurers-eye",
  "astral-stream",
  "the-grimoire",
  "realm-weaver",
  "token-forge",
  "campaign",
] as const;

const MODULE_GROUPS: Record<string, ModuleGroup> = {
  dashboard: "command",
  campaign: "command",
  "guardians-eye": "command",
  "visbys-portal": "live",
  "wardens-vanguard": "live",
  "wardens-echo": "live",
  "astral-stream": "live",
  "content-intelligence": "prep",
  omnarium: "prep",
  "encounter-crucible": "prep",
  "the-grimoire": "prep",
  "realm-weaver": "prep",
  "token-forge": "prep",
  "heros-loom": "players",
  "adventurers-eye": "players",
};

const MODULE_COPY: Record<string, string> = {
  dashboard: "One launcher for combat, maps, audio, reveals, and the library built from your own content.",
  "guardians-eye": "Your GM workspace—keep every session tool close without losing the room.",
  "visbys-portal": "Fog, tokens, and tactical play with every eye on the same map.",
  "wardens-vanguard": "Initiative, HP, conditions, and turn flow in rhythm with the story.",
  "content-intelligence": "Turn PDFs you own into structured monsters, lore, maps, and usable game content.",
  omnarium: "Browse monsters, spells, maps, and everything you have imported in one searchable library.",
  "encounter-crucible": "Build balanced encounters and load them straight into combat night.",
  "heros-loom": "Character sheets, story, art, and connection between sessions.",
  "wardens-echo": "Music, ambience, effects, and silence on your cue.",
  "adventurers-eye": "Cinematic visual reveals at the table—not another link lost in chat.",
  "astral-stream": "Broadcast handouts, slideshows, and Known Entities to the whole table.",
  "the-grimoire": "Notes, canvas, codex graph, and session planning in one connected workspace.",
  "realm-weaver": "Upload, calibrate, and prepare vision before the session starts.",
  "token-forge": "Create token art with frames and rings, then save it to your library.",
  campaign: "Create campaigns, send invitations, and keep the party together.",
};

function stamp(seconds: number): string {
  const safeSeconds = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
  return `${Math.floor(safeSeconds / 60)}:${String(safeSeconds % 60).padStart(2, "0")}`;
}

function FounderMessage({ onEngagement }: { onEngagement: (value: AudioEngagement) => void }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const openedAtRef = useRef<number | null>(null);
  const playedSecondsRef = useRef(0);
  const playStartedAtRef = useRef<number | null>(null);
  const milestonesRef = useRef(new Set<number>());
  const audioSessionIdRef = useRef<string | null>(null);
  const [open, setOpen] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);

  const addActiveListening = useCallback(() => {
    if (playStartedAtRef.current === null) return;
    playedSecondsRef.current += Math.max(0, (performance.now() - playStartedAtRef.current) / 1000);
    playStartedAtRef.current = null;
  }, []);

  const snapshot = useCallback((): AudioEngagement => {
    const audio = audioRef.current;
    const currentDuration = audio && Number.isFinite(audio.duration) ? audio.duration : duration;
    const activeSeconds =
      playStartedAtRef.current === null
        ? 0
        : Math.max(0, (performance.now() - playStartedAtRef.current) / 1000);
    const currentPosition = audio?.currentTime ?? position;
    return {
      positionSeconds: Math.round(currentPosition),
      listenedSeconds: Math.round(playedSecondsRef.current + activeSeconds),
      durationSeconds: Math.round(currentDuration || 0),
      percentComplete: currentDuration ? Math.round((currentPosition / currentDuration) * 100) : 0,
    };
  }, [duration, position]);

  const report = useCallback(
    (action: AudioAction) => {
      const engagement = snapshot();
      onEngagement(engagement);
      recordBetaEvent(AUDIO_EVENT_TYPES[action], {
        surface: "beta",
        ...(audioSessionIdRef.current ? { audio_session_id: audioSessionIdRef.current } : {}),
        position_seconds: engagement.positionSeconds,
        listened_seconds: engagement.listenedSeconds,
        duration_seconds: engagement.durationSeconds,
        percent_complete: engagement.percentComplete,
        seconds_since_open: openedAtRef.current
          ? Math.max(0, Math.round((Date.now() - openedAtRef.current) / 1000))
          : 0,
      });
    },
    [onEngagement, snapshot]
  );

  const startPlayback = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;
    try {
      await audio.play();
      sessionStorage.setItem(FOUNDER_STARTED_KEY, "1");
      if (playStartedAtRef.current === null) playStartedAtRef.current = performance.now();
      setPlaying(true);
      report("played");
    } catch {
      setPlaying(false);
    }
  }, [report]);

  const openPlayer = useCallback(() => {
    const openedAt = Date.now();
    const audioSessionId = crypto.randomUUID();
    openedAtRef.current = openedAt;
    audioSessionIdRef.current = audioSessionId;
    sessionStorage.setItem(FOUNDER_OPENED_AT_KEY, String(openedAt));
    sessionStorage.setItem(FOUNDER_SESSION_ID_KEY, audioSessionId);
    sessionStorage.removeItem(FOUNDER_STARTED_KEY);
    sessionStorage.removeItem(FOUNDER_COMPLETED_KEY);
    setOpen(true);
    report("opened");
    void startPlayback();
  }, [report, startPlayback]);

  const pausePlayback = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    addActiveListening();
    setPlaying(false);
    report("paused");
  }, [addActiveListening, report]);

  const stopPlayback = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    addActiveListening();
    report("stopped");
    audio.currentTime = 0;
    setPosition(0);
    setPlaying(false);
  }, [addActiveListening, report]);

  const closePlayer = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    addActiveListening();
    report("closed");
    audio.currentTime = 0;
    playedSecondsRef.current = 0;
    openedAtRef.current = null;
    milestonesRef.current.clear();
    setPosition(0);
    setPlaying(false);
    setOpen(false);
  }, [addActiveListening, report]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onMetadata = () => setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
    const onTime = () => {
      setPosition(audio.currentTime);
      const engagement = snapshot();
      onEngagement(engagement);
      [25, 50, 60, 75].forEach((milestone) => {
        const reached = milestone === 60
          ? audio.currentTime >= 60
          : engagement.percentComplete >= milestone;
        if (!reached || milestonesRef.current.has(milestone)) return;
        milestonesRef.current.add(milestone);
        recordBetaEvent("founder_message_progress", {
          surface: "beta",
          ...(audioSessionIdRef.current ? { audio_session_id: audioSessionIdRef.current } : {}),
          milestone: milestone === 60 ? "60_seconds" : `${milestone}_percent` as "25_percent" | "50_percent" | "75_percent",
          position_seconds: engagement.positionSeconds,
          listened_seconds: engagement.listenedSeconds,
          duration_seconds: engagement.durationSeconds,
          percent_complete: engagement.percentComplete,
          seconds_since_open: openedAtRef.current
            ? Math.max(0, Math.round((Date.now() - openedAtRef.current) / 1000))
            : 0,
        });
      });
    };
    const onEnded = () => {
      addActiveListening();
      sessionStorage.setItem(FOUNDER_COMPLETED_KEY, "1");
      setPlaying(false);
      setPosition(audio.duration || 0);
      report("completed");
    };

    audio.addEventListener("loadedmetadata", onMetadata);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("ended", onEnded);
    return () => {
      audio.removeEventListener("loadedmetadata", onMetadata);
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("ended", onEnded);
    };
  }, [addActiveListening, onEngagement, report, snapshot]);

  useEffect(() => {
    const onPageHide = () => {
      if (!open) return;
      const engagement = snapshot();
      onEngagement(engagement);
      recordBetaEvent("founder_message_left", {
        surface: "beta",
        ...(audioSessionIdRef.current ? { audio_session_id: audioSessionIdRef.current } : {}),
        position_seconds: engagement.positionSeconds,
        listened_seconds: engagement.listenedSeconds,
        duration_seconds: engagement.durationSeconds,
        percent_complete: engagement.percentComplete,
        seconds_since_open: openedAtRef.current
          ? Math.max(0, Math.round((Date.now() - openedAtRef.current) / 1000))
          : 0,
      });
    };
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  }, [onEngagement, open, snapshot]);

  return (
    <div className={`audio-pill${open ? " is-open" : ""}`}>
      {!open ? (
        <button className="audio-pill__trigger" type="button" onClick={openPlayer}>
          <span aria-hidden="true">▶</span>
          <span>A message from Nablex Black</span>
        </button>
      ) : (
        <div className="audio-pill__player">
          <button
            className="audio-control audio-control--toggle"
            type="button"
            aria-label={playing ? "Pause message" : "Play message"}
            onClick={playing ? pausePlayback : () => void startPlayback()}
          >
            {playing ? "❚❚" : "▶"}
          </button>
          <input
            className="audio-progress"
            type="range"
            min="0"
            max={duration || 1}
            value={Math.min(position, duration || 1)}
            step="0.1"
            aria-label="Audio progress"
            onChange={(event) => {
              const next = Number(event.target.value);
              if (audioRef.current) audioRef.current.currentTime = next;
              setPosition(next);
            }}
          />
          <span className="audio-time">{stamp(position)} / {duration ? stamp(duration) : "--:--"}</span>
          <button className="audio-control audio-control--stop" type="button" onClick={stopPlayback}>Stop</button>
          <button className="audio-control audio-control--close" type="button" aria-label="Close and reset message" onClick={closePlayer}>×</button>
        </div>
      )}
      <audio ref={audioRef} preload="metadata" src={publicAssetUrl("audio/nablex-black-message.mp3")} />
    </div>
  );
}

export function BetaShowcasePage() {
  const [filter, setFilter] = useState<"all" | ModuleGroup>("all");
  const [lightbox, setLightbox] = useState<BetaModule | null>(null);
  const engagementRef = useRef<AudioEngagement>({
    positionSeconds: 0,
    listenedSeconds: 0,
    durationSeconds: 0,
    percentComplete: 0,
  });

  const modules = useMemo(() => {
    const byId = new Map(BETA_MODULES.map((module) => [module.id, module]));
    return MODULE_ORDER.map((id) => byId.get(id)).filter((module): module is BetaModule => Boolean(module));
  }, []);

  const handleJoin = useCallback((placement: "nav" | "hero" | "gallery" | "finale") => {
    const openedAt = Number(sessionStorage.getItem(FOUNDER_OPENED_AT_KEY));
    const audioSessionId = sessionStorage.getItem(FOUNDER_SESSION_ID_KEY);
    const secondsSinceMessage = openedAt > 0 ? Math.max(0, Math.round((Date.now() - openedAt) / 1000)) : null;
    const engagement = engagementRef.current;
    recordBetaEvent("join_click", {
      placement,
      surface: "beta",
      founder_message_opened: secondsSinceMessage !== null,
      founder_message_started: sessionStorage.getItem(FOUNDER_STARTED_KEY) === "1",
      founder_message_completed: sessionStorage.getItem(FOUNDER_COMPLETED_KEY) === "1",
      within_5m_of_founder_message: secondsSinceMessage !== null && secondsSinceMessage <= 300,
      ...(audioSessionId ? { audio_session_id: audioSessionId } : {}),
      ...(secondsSinceMessage !== null ? { seconds_since_founder_message: secondsSinceMessage } : {}),
      audio_position_seconds: engagement.positionSeconds,
      audio_listened_seconds: engagement.listenedSeconds,
      audio_percent_complete: engagement.percentComplete,
    });
    recordEngagedVisitIfNeeded();
  }, []);

  const openModule = useCallback((module: BetaModule) => {
    recordModuleZoom(module.id, "gallery");
    recordEngagedVisitIfNeeded();
    setLightbox(module);
  }, []);

  useEffect(() => {
    recordPageView();
  }, []);

  useEffect(() => {
    document.body.style.overflow = lightbox ? "hidden" : "";
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setLightbox(null);
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightbox]);

  return (
    <>
      <IconField modules={ICON_FIELD_MODULES} />
      <div className="page-shell">
        <div className="stars" aria-hidden="true" />
        <nav className="nav wrap" aria-label="Primary navigation">
          <div className="brand">
            <span className="brand-mark">
              <span className="vortex" aria-hidden="true" />
              <img src={publicAssetUrl("images/logo.png")} alt="" />
            </span>
            <span className="brand-name">RealmWarden</span>
          </div>
          <div className="nav-actions">
            <FounderMessage onEngagement={(value) => { engagementRef.current = value; }} />
            <a className="button button--primary nav-signup" href={buildAppEntryUrl("sign-up", "nav")} onClick={() => handleJoin("nav")}>Join the Beta</a>
          </div>
        </nav>

        <main>
          <section className="hero wrap">
            <div className="hero-copy">
              <span className="eyebrow">Built for game night</span>
              <h1>Your TTRPG. <span>One operating system.</span></h1>
              <p>Bring the PDFs, maps, images, and stories you already own. RealmWarden connects prep, combat, maps, audio, and player reveals—without juggling multiple apps or scattering your game across disconnected tools.</p>
              <div className="hero-actions">
                <a className="button button--primary" href={buildAppEntryUrl("sign-up", "hero")} onClick={() => handleJoin("hero")}>Join the Free Beta</a>
                <a className="button button--secondary" href="#modules">See every module ↓</a>
              </div>
              <div className="microcopy">Built by players, for players · Bring the content you already own</div>
            </div>

            <div className="mosaic" aria-label="A preview of RealmWarden modules">
              <div className="shot shot--main">
                <img src={publicAssetUrl("images/modules/dashboard.png")} alt="RealmWarden dashboard" />
                <span className="shot-label">Your TTRPG OS</span>
              </div>
              <div className="shot shot--top">
                <img src={publicAssetUrl("images/modules/visbys-portal.png")} alt="Visby's Portal tactical map" />
                <span className="shot-label">Live maps</span>
              </div>
              <div className="shot shot--bottom">
                <img src={publicAssetUrl("images/modules/wardens-vanguard.png")} alt="Warden's Vanguard combat view" />
                <span className="shot-label">Run combat</span>
              </div>
            </div>
          </section>

          <section className="gallery-shell" id="modules">
            <div className="wrap">
              <div className="gallery-header">
                <div>
                  <span className="eyebrow">The platform, at a glance</span>
                  <h2>Every part of game night. One connected system.</h2>
                  <p>Click any card for the full-size view.</p>
                </div>
                <a className="button button--primary" href={buildAppEntryUrl("sign-up", "gallery")} onClick={() => handleJoin("gallery")}>Join the Beta</a>
              </div>

              <div className="filter-row" role="group" aria-label="Filter modules">
                {(["all", "command", "live", "prep", "players"] as const).map((value) => (
                  <button key={value} className={`filter${filter === value ? " is-active" : ""}`} type="button" onClick={() => setFilter(value)}>
                    {value === "all" ? "All tools" : value === "live" ? "Live play" : value[0].toUpperCase() + value.slice(1)}
                  </button>
                ))}
              </div>

              <div className="gallery">
                {modules.map((module) => {
                  const group = MODULE_GROUPS[module.id] ?? "prep";
                  if (filter !== "all" && group !== filter) return null;
                  return (
                    <button key={module.id} className={`module-card module-card--${module.id}`} type="button" onClick={() => openModule(module)} style={{ "--accent": module.color } as CSSProperties}>
                      <span className="module-image"><img src={module.image ?? ""} alt={`${module.name} preview`} /></span>
                      <span className="module-body">
                        <span className="module-kicker">{group === "live" ? "Live play" : group}{module.id === "token-forge" ? " + players" : ""}</span>
                        <span className="module-title">{module.name.replace("The Warden's Vanguard", "Warden's Vanguard")}</span>
                        <span className="module-description">{MODULE_COPY[module.id] ?? module.tease}</span>
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="gallery-note">
                <div><strong>Ready to bring your table together?</strong><span>Join the free beta and start running your games from one connected system.</span></div>
                <a className="button button--primary" href={buildAppEntryUrl("sign-up", "finale")} onClick={() => handleJoin("finale")}>Join the Free Beta</a>
              </div>
            </div>
          </section>
        </main>

        <footer className="site-footer">
          <div className="wrap">© 2026 RealmWarden LLC · <a href="/privacy-policy">Privacy</a> · <a href="/terms">Terms</a></div>
        </footer>
      </div>

      {lightbox?.image && (
        <div className="lightbox" role="dialog" aria-modal="true" aria-label={`${lightbox.name} full-size view`} onClick={(event) => { if (event.target === event.currentTarget) setLightbox(null); }}>
          <button className="lightbox-close" type="button" aria-label="Close full-size view" onClick={() => setLightbox(null)}>×</button>
          <figure><img src={lightbox.image} alt={`${lightbox.name} full-size view`} /><figcaption>{lightbox.name}</figcaption></figure>
        </div>
      )}
    </>
  );
}
